/**
 * The account holder's own status: the dot, the hook behind it, and the picker.
 *
 * ── Not the other presence ──────────────────────────────────────────────────
 *
 * `api.presence(projectId)` in web/src/api.ts is a different feature with the
 * same name: the anonymous reviewer's per-project heartbeat, which is what
 * drives `usePulse` and the "LIVE · N REVIEWERS IN" pill. It is scoped to a
 * project and has no Mauvely account behind it.
 *
 * This is the account-level one — available, away, busy, do not disturb —
 * shared with every other app in the suite. Everything exported here is named
 * `…AccountPresence…` so the two cannot be confused at a call site, and
 * `setPresence` from the shared client is deliberately **not** re-exported: the
 * only thing in this app that sets it is the picker below.
 *
 * ── What is here and what is shared ─────────────────────────────────────────
 *
 * The part that carries meaning — which colour, and whether do-not-disturb gets
 * its bar — is `presenceDot()` in `@shared/presence`, so five apps cannot
 * disagree about it. What is here is the elements around it, because five apps
 * draw five differently-shaped chips and a shared component would arrive with
 * five props describing where it was.
 *
 * The socket itself is `@shared/presence-client`, started once by `AppProvider`
 * in web/src/state.tsx.
 */

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type React from 'react';

import {
  getPresence,
  getPresenceOverride,
  setPresence,
  subscribePresence
} from '@shared/presence-client';
import {
  isSettablePresence,
  presenceDot,
  PRESENCE_LABEL,
  PRESENCE_STATES,
  type Presence
} from '@shared/presence';

import { Avatar } from './rail/ThreadCard';
import { mono, RADIUS } from '../theme';

/**
 * The four a person may choose. `offline` and `unknown` are observations rather
 * than decisions and are deliberately absent — being unreachable while sitting
 * at your desk is what "away" is for.
 */
const CHOICES = PRESENCE_STATES.filter(isSettablePresence);

/**
 * What to draw, and what was chosen.
 *
 * Both are plain strings, so the snapshot is referentially stable and
 * `useSyncExternalStore` has nothing to loop on.
 *
 * The two differ, and the picker needs both: somebody who chose "available" and
 * then went to lunch is *shown* as away, and a tick against "Away" in the menu
 * would be telling them they chose something they did not.
 */
export function useAccountPresence(): { presence: Presence; override: Presence | null } {
  const presence = useSyncExternalStore(subscribePresence, getPresence, () => 'unknown' as const);
  const override = useSyncExternalStore(subscribePresence, getPresenceOverride, () => null);
  return { presence, override };
}

/**
 * A dot in the corner of an avatar.
 *
 * The caller supplies the positioned wrapper — this is `position: absolute` and
 * needs something to be absolute inside. `ring` is the **chip's** background
 * rather than the page's, because the dot cuts itself out of whatever it sits
 * on: since 2026-09-09 that is the app ground the header sits on, and a dot
 * ringed in the wrong colour has a visible seam.
 *
 * Renders nothing for `unknown`, which is what a page that has not heard yet
 * shows.
 */
export function AccountPresenceDot({
  presence,
  size = 9,
  ring
}: {
  presence: Presence;
  size?: number;
  ring: string;
}) {
  const style = presenceDot(presence, size, ring);
  if (!style) return null;

  return (
    <span aria-hidden="true" style={style.dot as React.CSSProperties}>
      {style.bar && <span style={style.bar as React.CSSProperties} />}
    </span>
  );
}

/**
 * The signed-in person's chip in the author header, and the picker behind it.
 *
 * **The one place in this app that draws the dot.** `Avatar` is shared with six
 * reviewer call sites and is `aria-hidden`, so the dot is positioned by the
 * wrapper here instead of by a prop on it — a reviewer in the comment rail is
 * somebody with no Mauvely account and has no status to show.
 *
 * The popover is the bell's, which sits immediately to the left of this in the
 * same header: same offset, same surface, same outside-click and Escape.
 */
export function AccountPresenceChip({ name, colour }: { name: string; colour: string }) {
  const { presence, override } = useAccountPresence();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (next: Presence | null) => {
    setOpen(false);
    void setPresence(next);
  };

  // The header shows a first name and the avatar is `aria-hidden`, so the whole
  // name and the current status live on the button.
  const label = PRESENCE_LABEL[presence];

  return (
    <div ref={root} style={{ position: 'relative' }}>
      <button
        type="button"
        title={name}
        aria-label={label ? `${name} · ${label}` : name}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          padding: 0,
          background: 'none',
          border: 0,
          cursor: 'pointer'
        }}
      >
        <span style={{ position: 'relative', display: 'inline-flex', flex: 'none' }}>
          <Avatar name={name} colour={colour} size={22} />
          <AccountPresenceDot presence={presence} size={8} ring="var(--app-ground)" />
        </span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{name.split(' ')[0]}</span>
      </button>

      {open && (
        <div
          className="mv-pop"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 190,
            padding: 4,
            zIndex: 60,
            background: 'var(--surface-raised, var(--surface-card))',
            border: '1px solid var(--border-subtle)',
            borderRadius: RADIUS.card,
            boxShadow: '0 16px 40px rgba(8,6,16,.5)'
          }}
        >
          {CHOICES.map((choice) => {
            const chosen = override === choice;
            return (
              <button
                key={choice}
                type="button"
                aria-pressed={chosen}
                onClick={() => choose(chosen ? null : choice)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: RADIUS.chip,
                  border: 0,
                  background: chosen ? 'var(--primary-soft)' : 'none',
                  color: chosen ? 'var(--primary)' : 'var(--text-body)',
                  fontSize: 12.5,
                  textAlign: 'left',
                  cursor: 'pointer'
                }}
              >
                {/* The dot is `position: absolute`, so it needs something to be
                    absolute inside even when it is not on an avatar. */}
                <span
                  style={{
                    position: 'relative',
                    display: 'inline-flex',
                    width: 9,
                    height: 9,
                    flex: 'none'
                  }}
                >
                  <AccountPresenceDot
                    presence={choice}
                    size={9}
                    ring={
                      chosen ? 'var(--primary-soft)' : 'var(--surface-raised, var(--surface-card))'
                    }
                  />
                </span>
                {PRESENCE_LABEL[choice]}
              </button>
            );
          })}

          {override && (
            <div
              style={{
                marginTop: 4,
                paddingTop: 4,
                borderTop: '1px solid var(--border-subtle)'
              }}
            >
              <button
                type="button"
                onClick={() => choose(null)}
                style={{
                  ...mono,
                  fontSize: 9,
                  display: 'block',
                  width: '100%',
                  padding: '6px 10px',
                  background: 'none',
                  border: 0,
                  color: 'var(--primary)',
                  textAlign: 'left',
                  cursor: 'pointer'
                }}
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
